import { useState, useEffect, useRef } from "react";
import { GiftType, SeaObjectType } from "../types/treasure-hunt";
import {
  GIFT_NAMES,
  INTERACTIVE_OBJECTS,
  GRID_COLS,
  GRID_ROWS,
  PLAYER_WATER_OBJECTS,
  PLAYER_SAND_OBJECTS,
  AGE_OBJECTS,
} from "../constants/treasure-hunt";
import { GameField } from "../components/treasure-hunt/GameField";
import { WelcomeModal } from "../components/treasure-hunt/WelcomeModal";
import { RulesModal } from "../components/treasure-hunt/RulesModal";
import { ElementsLegend } from "../components/treasure-hunt/ElementsLegend";
import { PromoCodeModal } from "../components/treasure-hunt/PromoCodeModal";
import { ActionPanel } from "../components/treasure-hunt/ActionPanel";
import { MessageModal } from "../components/treasure-hunt/MessageModal";
import { LanguageToggle } from "../components/treasure-hunt/LanguageToggle";
import { SelectMessageTypeModal } from "../components/treasure-hunt/SelectMessageTypeModal";
import { MapViewport } from "../components/treasure-hunt/MapViewport";
import { LeaveMessageTextOnlyModal } from "../components/treasure-hunt/LeaveMessageTextOnlyModal";
import {
  LeaveAcquaintanceFormModal,
  AcquaintanceFormData,
  Gender
} from "../components/treasure-hunt/LeaveAcquaintanceFormModal";
import { CompassModal } from "../components/treasure-hunt/CompassModal";
import { useTreasureHunt } from "../contexts/TreasureHuntContext";
import { treasureHuntApi } from "../services/treasureHunt.api";
import { usePageMeta } from "../hooks/usePageMeta";

const STORAGE_KEYS = {
  LANGUAGE: "treasureHunt_language",
  WELCOME_SHOWN: "treasureHunt_welcomeShown",
} as const;

export const TreasurePage = () => {
  const { tileMap, setTileMap } = useTreasureHunt();

  // Устанавливаем title и favicon для страницы
  usePageMeta({
    title: 'Treasure Hunt',
    favicon: {
      ico: '/favicon/treasure-hunt/favicon.ico',
      png16: '/favicon/treasure-hunt/favicon-16x16.png',
      png32: '/favicon/treasure-hunt/favicon-32x32.png',
      png192: '/favicon/treasure-hunt/android-chrome-192x192.png',
      png512: '/favicon/treasure-hunt/android-chrome-512x512.png',
      apple: '/favicon/treasure-hunt/apple-touch-icon.png',
    },
  });

  const [hoveredTile, setHoveredTile] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [tooltipAge, setTooltipAge] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{
    x: number;
    y: number;
  }>({ x: 0, y: 0 });

  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [showActionPanel, setShowActionPanel] = useState(false);
  const [code, setCode] = useState("");
  const [giftsRevealed, setGiftsRevealed] = useState(false);
  const [simulationLanguage, setSimulationLanguage] = useState<"ka" | "en">(
    "ka"
  );
  const [tileSize, setTileSize] = useState(35);

  const [showMessageModal, setShowMessageModal] = useState(false);
  const [currentMessage, setCurrentMessage] = useState({
    title: "",
    message: "",
  });

  const [openedItems, setOpenedItems] = useState<Set<string>>(new Set());
  const [hasOpenedCloud, setHasOpenedCloud] = useState(false); // Флаг открытия облака для текущего кода

  // Состояния для компаса
  const [showCompassModal, setShowCompassModal] = useState(false);
  const [compassAngle, setCompassAngle] = useState(0);
  const [compassPosition, setCompassPosition] = useState({ x: 0, y: 0 });

  // Состояние загрузки карты
  const [isMapLoading, setIsMapLoading] = useState(true);

  // Состояния для размещения посланий и знакомств
  const [showLeaveMessageTextModal, setShowLeaveMessageTextModal] = useState(false);
  const [showLeaveAcquaintanceFormModal, setShowLeaveAcquaintanceFormModal] = useState(false);
  const [isPlacingMessage, setIsPlacingMessage] = useState(false); // Режим размещения сообщения
  const [isPlacingAcquaintance, setIsPlacingAcquaintance] = useState(false); // Режим размещения знакомства
  const [messageText, setMessageText] = useState("");
  const [acquaintanceData, setAcquaintanceData] = useState<AcquaintanceFormData | null>(null);
  const [isProcessingClick, setIsProcessingClick] = useState(false);

  // Используем useRef для синхронного отслеживания открытых элементов
  const openedItemsRef = useRef<Set<string>>(new Set());

  // Вычисление размера клеток при изменении размера окна
  useEffect(() => {
    const calculateTileSize = () => {
      const windowWidth = window.innerWidth;

      // Если экран меньше 1800px - оставляем клетки 35px для скроллинга
      // На планшетах и смартфонах клетки останутся различимыми
      if (windowWidth < 1800) {
        setTileSize(35);
        return;
      }

      // На больших экранах растягиваем карту на всю ширину
      const tileSizeByWidth = windowWidth / GRID_COLS;
      setTileSize(tileSizeByWidth);
    };

    calculateTileSize();
    window.addEventListener("resize", calculateTileSize);

    return () => window.removeEventListener("resize", calculateTileSize);
  }, []);

  // Автоматическая загрузка карты с сервера
  useEffect(() => {
    const loadMap = async () => {
      setIsMapLoading(true);
      try {
        const response = await treasureHuntApi.getMap();
        if (response.success && response.data) {
          setTileMap(response.data);
        }
      } catch (err) {
        console.error("Карта не найдена на сервере, используется пустая карта");
      } finally {
        setIsMapLoading(false);
      }
    };

    loadMap();
  }, [setTileMap]);

  // Инициализация при загрузке страницы
  useEffect(() => {
    // Загружаем язык из localStorage
    const savedLanguage = localStorage.getItem(STORAGE_KEYS.LANGUAGE) as
      | "ka"
      | "en"
      | null;
    if (savedLanguage) {
      setSimulationLanguage(savedLanguage);
    }

    // Проверяем, показывалось ли приветствие
    const welcomeShown = localStorage.getItem(STORAGE_KEYS.WELCOME_SHOWN);
    if (!welcomeShown) {
      setShowWelcomeModal(true);
    } else {
      // Если приветствие уже было, показываем код сразу
      setShowCodeModal(true);
    }
  }, []);

  // Синхронизируем ref с state openedItems
  useEffect(() => {
    openedItemsRef.current = new Set(openedItems);
  }, [openedItems]);

  // Сохраняем язык при изменении
  const handleLanguageChange = (lang: "ka" | "en") => {
    setSimulationLanguage(lang);
    localStorage.setItem(STORAGE_KEYS.LANGUAGE, lang);
  };

  // Закрытие приветственного окна
  const handleWelcomeClose = () => {
    setShowWelcomeModal(false);
    localStorage.setItem(STORAGE_KEYS.WELCOME_SHOWN, "true");
    // После закрытия приветствия показываем код
    setShowCodeModal(true);
  };

  // Обработка ввода кода
  const handleCodeSubmit = (enteredCode: string) => {
    // Проверка уже выполнена в PromoCodeModal
    setCode(enteredCode);
    setHasOpenedCloud(false);
    setShowCodeModal(false);
    setShowActionPanel(true);
  };

  // Обработка выбора "Открыть послания"
  const handleOpenGifts = async () => {
    // Отмечаем код как использованный сразу при выборе действия
    if (code) {
      try {
        await treasureHuntApi.markCodeAsUsed(code);
      } catch (err) {
        console.error("Не удалось отметить код как использованный:", err);
      }
    }
    setShowActionPanel(false);
    setGiftsRevealed(true);
  };

  // Обработка выбора "Оставить послание"
  const handleLeaveMessage = async () => {
    // Отмечаем код как использованный сразу при выборе действия
    if (code) {
      try {
        await treasureHuntApi.markCodeAsUsed(code);
      } catch (err) {
        console.error("Не удалось отметить код как использованный:", err);
      }
    }
    setShowActionPanel(false);
    setShowLeaveMessageTextModal(true);
  };

  // Обработка выбора "Оставить знакомство"
  const handleLeaveAcquaintance = async () => {
    // Отмечаем код как использованный сразу при выборе действия
    if (code) {
      try {
        await treasureHuntApi.markCodeAsUsed(code);
      } catch (err) {
        console.error("Не удалось отметить код как использованный:", err);
      }
    }
    setShowActionPanel(false);
    setShowLeaveAcquaintanceFormModal(true);
  };

  // Обработка сохранения текста сообщения
  const handleSaveMessageText = (text: string) => {
    setMessageText(text);
    setShowLeaveMessageTextModal(false);
    setIsPlacingMessage(true);
    setGiftsRevealed(true); // Показываем карту для размещения
  };

  // Обработка сохранения данных знакомства
  const handleSaveAcquaintanceData = (data: AcquaintanceFormData) => {
    setAcquaintanceData(data);
    setShowLeaveAcquaintanceFormModal(false);
    setIsPlacingAcquaintance(true);
    setGiftsRevealed(true); // Показываем карту для размещения
  };

  // Функция для поиска ближайшего сундука и расчета угла компаса
  const findNearestInteractiveObject = (
    currentX: number,
    currentY: number
  ): number | null => {
    let nearestDistance = Infinity;
    let nearestX = 0;
    let nearestY = 0;
    let found = false;

    // Ищем ближайший сундук (только chest_sand и chest_sea)
    const chestTypes = [SeaObjectType.ISLAND_SAND_CHEST, SeaObjectType.CHEST_SEA];

    for (let y = 0; y < GRID_ROWS; y++) {
      for (let x = 0; x < GRID_COLS; x++) {
        const tile = tileMap[y][x];
        const itemKey = `${x}-${y}`;

        // Пропускаем уже открытые элементы и текущую клетку
        if (openedItems.has(itemKey) || (x === currentX && y === currentY)) {
          continue;
        }

        // Проверяем, является ли tileCode одним из типов сундуков
        const isChestTile = chestTypes.includes(tile.tileCode);

        // Проверяем, есть ли seaObject с типом сундука
        const hasChestObject = tile.seaObject && chestTypes.includes(tile.seaObject.type);

        if (isChestTile || hasChestObject) {
          // Вычисляем расстояние (Евклидово расстояние)
          const distance = Math.sqrt(
            Math.pow(x - currentX, 2) + Math.pow(y - currentY, 2)
          );

          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestX = x;
            nearestY = y;
            found = true;
          }
        }
      }
    }

    if (!found) {
      return null; // Нет доступных сундуков
    }

    // Вычисляем угол от текущей позиции к ближайшему сундуку
    const deltaX = nearestX - currentX;
    const deltaY = nearestY - currentY;

    // Вычисляем угол в радианах, затем переводим в градусы
    // Math.atan2 возвращает угол от -π до π
    let angleRad = Math.atan2(deltaX, -deltaY); // -deltaY потому что Y растет вниз
    let angleDeg = (angleRad * 180) / Math.PI;

    // Нормализуем угол в диапазон 0-360
    if (angleDeg < 0) {
      angleDeg += 360;
    }

    return angleDeg;
  };

  // Функция для поиска случайной свободной клетки нужного типа под облаком
  const findRandomEmptyTile = (tileType: "water" | "sand"): { x: number; y: number } | null => {
    const emptyCells: { x: number; y: number }[] = [];
    const targetTileCode = tileType === "water" ? SeaObjectType.WATER : SeaObjectType.SAND_ISLAND;

    // Собираем все свободные клетки нужного типа с облаками
    for (let y = 0; y < GRID_ROWS; y++) {
      for (let x = 0; x < GRID_COLS; x++) {
        const tile = tileMap[y][x];
        const hasAnyContent = tile.gift || tile.seaObject;
        const isCleanTile = tile.tileCode === targetTileCode;
        const hasClouds = tile.hasClouds === true; // Проверяем наличие облаков

        // Клетка должна быть чистой, нужного типа И под облаком
        if (!hasAnyContent && isCleanTile && hasClouds) {
          emptyCells.push({ x, y });
        }
      }
    }

    // Если нашли свободные клетки с облаками - выбираем случайную
    if (emptyCells.length > 0) {
      const randomIndex = Math.floor(Math.random() * emptyCells.length);
      return emptyCells[randomIndex];
    }

    return null;
  };


  // Функция размещения бутылки с сообщением
  const placeMessageBottle = async (x: number, y: number) => {
    const tile = tileMap[y][x];

    // Определяем тип бутылки по типу клетки
    const bottleType = tile.tileCode === SeaObjectType.WATER
      ? SeaObjectType.BOTTLE
      : SeaObjectType.BOTTLE_SAND;

    const newTile = {
      tileCode: tile.tileCode,
      seaObject: {
        type: bottleType,
        rotation: 0,
        text: messageText,
      },
      hasClouds: tile.hasClouds,
    };

    const newMap = tileMap.map((row, rowIndex) =>
      row.map((t, colIndex) => {
        if (rowIndex === y && colIndex === x) {
          return newTile;
        }
        return { ...t };
      })
    );

    setTileMap(newMap);

    // Сохраняем на сервер
    try {
      await treasureHuntApi.saveMap(newMap);
    } catch (err) {
      console.error("Не удалось сохранить послание на сервере:", err);
    }

    // Сохраняем историю
    try {
      await saveActivationHistory(
        x,
        y,
        newTile,
        simulationLanguage === "ka" ? "შეტყობინება დატოვებულია" : "Message Left",
        messageText
      );
    } catch (err) {
      console.error("Не удалось сохранить историю:", err);
    }

    // Сбрасываем состояния
    setIsPlacingMessage(false);
    setMessageText("");

    // Показываем подтверждение
    setCurrentMessage({
      title: simulationLanguage === "ka" ? "შეტყობინება დატოვებულია!" : "Message Left!",
      message: simulationLanguage === "ka"
        ? "თქვენი შეტყობინება წარმატებით განთავსდა რუკაზე."
        : "Your message has been successfully placed on the map.",
    });
    setShowMessageModal(true);
  };

  // Функция выбора случайного персонажа для знакомства
  const getRandomAcquaintanceCharacter = (gender: Gender, isWater: boolean): SeaObjectType => {
    if (gender === "male") {
      if (isWater) {
        return SeaObjectType.MAN_SEA_LIES;
      } else {
        // Случайный выбор между двумя вариантами на песке
        const sandOptions = [SeaObjectType.MAN_SAND_LIES, SeaObjectType.MAN_SAND];
        return sandOptions[Math.floor(Math.random() * sandOptions.length)];
      }
    } else {
      if (isWater) {
        return SeaObjectType.WOMAN_SEA_LIES;
      } else {
        // Случайный выбор между двумя вариантами на песке
        const sandOptions = [SeaObjectType.WOMAN_SAND_LIES, SeaObjectType.WOMAN_SAND_LIES2];
        return sandOptions[Math.floor(Math.random() * sandOptions.length)];
      }
    }
  };

  // Функция размещения знакомства
  const placeAcquaintance = async (x: number, y: number) => {
    if (!acquaintanceData) return;

    const tile = tileMap[y][x];
    const isWater = tile.tileCode === SeaObjectType.WATER;

    // Получаем случайный персонаж нужного пола
    const characterType = getRandomAcquaintanceCharacter(acquaintanceData.gender, isWater);

    // Формируем текст с возрастом и инстаграмом
    const fullText = `👤 ${simulationLanguage === "ka" ? "ასაკი" : "Age"}: ${acquaintanceData.age}\n📷 Instagram: ${acquaintanceData.instagram}\n\n${acquaintanceData.text}`;

    const newTile = {
      tileCode: tile.tileCode,
      seaObject: {
        type: characterType,
        rotation: 0,
        text: fullText,
        age: acquaintanceData.age,
      },
      hasClouds: tile.hasClouds,
    };

    const newMap = tileMap.map((row, rowIndex) =>
      row.map((t, colIndex) => {
        if (rowIndex === y && colIndex === x) {
          return newTile;
        }
        return { ...t };
      })
    );

    setTileMap(newMap);

    // Сохраняем на сервер
    try {
      await treasureHuntApi.saveMap(newMap);
    } catch (err) {
      console.error("Не удалось сохранить знакомство на сервере:", err);
    }

    // Сохраняем историю
    try {
      await saveActivationHistory(
        x,
        y,
        newTile,
        simulationLanguage === "ka" ? "ახალი გაცნობა" : "New Acquaintance",
        fullText
      );
    } catch (err) {
      console.error("Не удалось сохранить историю:", err);
    }

    // Сбрасываем состояния
    setIsPlacingAcquaintance(false);
    setAcquaintanceData(null);

    // Показываем подтверждение
    setCurrentMessage({
      title: simulationLanguage === "ka" ? "გაცნობა დატოვებულია!" : "Acquaintance Left!",
      message: simulationLanguage === "ka"
        ? "თქვენი გაცნობა წარმატებით განთავსდა რუკაზე."
        : "Your acquaintance has been successfully placed on the map.",
    });
    setShowMessageModal(true);
  };

  // Обработка отмены модальных окон
  const handleCancelLeaveMessage = () => {
    setShowLeaveMessageTextModal(false);
    setShowLeaveAcquaintanceFormModal(false);
    setIsPlacingMessage(false);
    setIsPlacingAcquaintance(false);
    setMessageText("");
    setAcquaintanceData(null);
    setGiftsRevealed(false);
    setShowActionPanel(true);
  };

  // Обработка отмены
  const handleCancelAction = () => {
    setShowActionPanel(false);
    setShowCodeModal(true);
    setCode("");
  };

  // Обработка закрытия модалки сообщения - возврат к коду
  const handleCloseMessage = () => {
    setShowMessageModal(false);
    setGiftsRevealed(false);
    setShowCodeModal(true);
    setCode("");
    setHasOpenedCloud(false); // Сбрасываем флаг при возврате к вводу кода
  };

  // Обработка закрытия компаса - возврат к коду
  const handleCloseCompass = () => {
    setShowCompassModal(false);
    setGiftsRevealed(false);
    setShowCodeModal(true);
    setCode("");
    setHasOpenedCloud(false); // Сбрасываем флаг при возврате к вводу кода
  };

  // Функция для сохранения истории активации
  const saveActivationHistory = async (
    x: number,
    y: number,
    tile: (typeof tileMap)[0][0],
    messageTitle: string,
    messageContent: string
  ) => {
    if (!code) {
      console.warn("Код не установлен, история не сохраняется");
      return;
    }

    try {
      const historyData: any = {
        code: code,
        tileData: {
          x,
          y,
          tileCode: tile.tileCode, // Сохраняем конкретный код клетки
        },
        messageTitle,
        messageContent,
      };

      // Добавляем информацию о подарке или объекте
      if (tile.gift) {
        historyData.tileData.giftType = tile.gift.type;
        historyData.tileData.text = tile.gift.text;
      } else if (tile.seaObject) {
        historyData.tileData.seaObjectType = tile.seaObject.type;
        historyData.tileData.text = tile.seaObject.text;
      }

      console.log("Сохраняем историю активации:", historyData);
      const response = await treasureHuntApi.saveActivationHistory(historyData);
      console.log("Ответ от сервера:", response);

      if (!response.success) {
        console.error("Ошибка при сохранении истории:", response.message);
      }
    } catch (err) {
      console.error("Не удалось сохранить историю активации:", err);
    }
  };

  // Обработка клика на клетку
  const handleTileClick = async (x: number, y: number) => {
    if (!giftsRevealed || isProcessingClick) return;

    const tile = tileMap[y][x];

    // Режим размещения сообщения (бутылка) - только на пустые клетки БЕЗ облаков
    if (isPlacingMessage && messageText) {
      setIsProcessingClick(true);

      // Проверяем, что на клетке НЕТ облака
      if (tile.hasClouds) {
        console.log("❌ Нельзя размещать на клетках с облаками");
        setIsProcessingClick(false);
        return;
      }

      // Проверяем, что на клетке нет подарка или объекта
      const hasAnyContent = tile.gift || tile.seaObject;
      if (hasAnyContent) {
        console.log("❌ Клетка занята");
        setIsProcessingClick(false);
        return;
      }

      // Проверяем тип клетки (только чистая вода или песок)
      const allowedTileCodes = [SeaObjectType.WATER, SeaObjectType.SAND_ISLAND];
      const isCleanTile = allowedTileCodes.includes(tile.tileCode);

      if (!isCleanTile) {
        console.log("❌ Можно размещать только на чистой воде или песке");
        setIsProcessingClick(false);
        return;
      }

      console.log("✅ Размещаем бутылку с сообщением");
      await placeMessageBottle(x, y);
      setIsProcessingClick(false);
      return;
    }

    // Режим размещения знакомства - только на пустые клетки БЕЗ облаков
    if (isPlacingAcquaintance && acquaintanceData) {
      setIsProcessingClick(true);

      // Проверяем, что на клетке НЕТ облака
      if (tile.hasClouds) {
        console.log("❌ Нельзя размещать на клетках с облаками");
        setIsProcessingClick(false);
        return;
      }

      // Проверяем, что на клетке нет подарка или объекта
      const hasAnyContent = tile.gift || tile.seaObject;
      if (hasAnyContent) {
        console.log("❌ Клетка занята");
        setIsProcessingClick(false);
        return;
      }

      // Проверяем тип клетки (только чистая вода или песок)
      const allowedTileCodes = [SeaObjectType.WATER, SeaObjectType.SAND_ISLAND];
      const isCleanTile = allowedTileCodes.includes(tile.tileCode);

      if (!isCleanTile) {
        console.log("❌ Можно размещать только на чистой воде или песке");
        setIsProcessingClick(false);
        return;
      }

      console.log("✅ Размещаем знакомство");
      await placeAcquaintance(x, y);
      setIsProcessingClick(false);
      return;
    }

    // Далее обрабатываем клики на облака и интерактивные объекты
    if (isProcessingClick) return;

    // НОВАЯ ЛОГИКА: Если на клетке есть облако - убираем его и открываем содержимое
    if (tile.hasClouds) {
      // Проверяем, не было ли уже открыто облако с этим кодом
      if (hasOpenedCloud) {
        console.log("❌ С этим кодом уже было открыто облако");
        // Показываем сообщение пользователю
        setCurrentMessage({
          title:
            simulationLanguage === "ka"
              ? "კოდი უკვე გამოყენებულია"
              : "Code Already Used",
          message:
            simulationLanguage === "ka"
              ? "ამ კოდით უკვე გახსენით ერთი უჯრედი. თითოეული კოდით შესაძლებელია მხოლოდ ერთი უჯრედის გახსნა."
              : "You have already opened one cell with this code. Each code can only open one cell.",
        });
        setShowMessageModal(true);
        return;
      }

      setIsProcessingClick(true);
      setHasOpenedCloud(true); // Помечаем, что облако открыто

      // Убираем облако с клетки
      const newMap = tileMap.map((row, rowIndex) =>
        row.map((t, colIndex) => {
          if (rowIndex === y && colIndex === x) {
            return { ...t, hasClouds: false };
          }
          return { ...t };
        })
      );

      setTileMap(newMap);

      // Сохраняем обновленную карту на сервере
      try {
        await treasureHuntApi.saveMap(newMap);
      } catch (err) {
        console.error("Не удалось сохранить изменения карты на сервере:", err);
      }

      // Проверяем, есть ли под облаком подарок или интерактивный объект
      let messageData: { title: string; message: string } | null = null;

      if (tile.gift) {
        messageData = getMessageForGift(tile.gift.type, tile.gift.text);
      } else if (
        tile.seaObject &&
        INTERACTIVE_OBJECTS.includes(tile.seaObject.type)
      ) {
        messageData = getMessageForSeaObject(
          tile.seaObject.type,
          tile.seaObject.text,
          tile.seaObject.age
        );
      }

      // Если под облаком что-то было - показываем сообщение и удаляем объект
      if (messageData) {
        console.log("[TreasurePage] Message found under cloud, code:", code);

        const itemKey = `${x}-${y}`;
        openedItemsRef.current.add(itemKey);
        setOpenedItems((prev) => new Set(prev).add(itemKey));

        // Сохраняем историю активации с открытым посланием
        await saveActivationHistory(
          x,
          y,
          tile,
          messageData.title,
          messageData.message
        );

        setCurrentMessage(messageData);
        setShowMessageModal(true);

        // Удаляем элемент с карты
        const finalMap = newMap.map((row, rowIndex) =>
          row.map((tile, colIndex) => {
            if (rowIndex === y && colIndex === x) {
              // Используем оригинальный tileCode клетки, чтобы сохранить базовый тип (вода/песок)
              // Это важно, чтобы песок оставался песком, а вода - водой
              let baseTileCode = tile.tileCode;

              // Если tileCode не установлен или это интерактивный объект, определяем базовый тип
              if (!tile.tileCode || tile.tileCode === SeaObjectType.ISLAND_SAND_CHEST || tile.tileCode === SeaObjectType.CHEST_SEA) {
                // Если это сундук на воде - всегда возвращаем воду
                if (tile.tileCode === SeaObjectType.CHEST_SEA) {
                  baseTileCode = SeaObjectType.WATER;
                } else if (tile.seaObject) {
                  if (PLAYER_SAND_OBJECTS.includes(tile.seaObject.type)) {
                    baseTileCode = SeaObjectType.SAND_ISLAND;
                  } else if (PLAYER_WATER_OBJECTS.includes(tile.seaObject.type)) {
                    baseTileCode = SeaObjectType.WATER;
                  }
                } else if (tile.gift) {
                  baseTileCode = SeaObjectType.SAND_ISLAND;
                }
              }

              // Очищаем клетку - возвращаем к базовому типу без облака
              return { tileCode: baseTileCode, hasClouds: false };
            }
            return { ...tile };
          })
        );

        setTileMap(finalMap);

        // Сохраняем финальную версию карты на сервере
        try {
          await treasureHuntApi.saveMap(finalMap);
        } catch (err) {
          console.error("Не удалось сохранить изменения карты на сервере:", err);
        }
      } else {
        // Под облаком ничего нет - показываем компас с направлением к ближайшему объекту
        const angle = findNearestInteractiveObject(x, y);

        if (angle !== null) {
          // Находим элемент клетки на экране
          const tileElement = document.querySelector(
            `[data-tile-x="${x}"][data-tile-y="${y}"]`
          );

          if (tileElement) {
            const rect = tileElement.getBoundingClientRect();
            // Позиция - центр клетки
            const tileCenterX = rect.left + rect.width / 2;
            const tileCenterY = rect.top + rect.height / 2;

            setCompassAngle(angle);
            setCompassPosition({ x: tileCenterX, y: tileCenterY });
            setShowCompassModal(true);
          } else {
            // Если не нашли элемент - показываем в центре экрана
            const screenX = window.innerWidth / 2;
            const screenY = window.innerHeight / 2;

            setCompassAngle(angle);
            setCompassPosition({ x: screenX, y: screenY });
            setShowCompassModal(true);
          }
        } else {
          // Нет доступных сундуков - показываем сообщение
          setCurrentMessage({
            title:
              simulationLanguage === "ka"
                ? "ცარიელი ღრუბელი 🌥️"
                : "Empty Cloud 🌥️",
            message:
              simulationLanguage === "ka"
                ? "ამ ღრუბლის ქვეშ არაფერი არ იყო. რუკაზე აღარ არის ხელმისაწვდომი ყუთები."
                : "There was nothing under this cloud. There are no more available chests on the map.",
          });
          setShowMessageModal(true);
        }
      }

      setIsProcessingClick(false);
      return;
    }

    // Если облака нет - проверяем открытые элементы (старая логика для клеток без облаков)
    const itemKey = `${x}-${y}`;

    // Проверяем, не был ли этот элемент уже открыт (используем ref для синхронной проверки)
    if (openedItemsRef.current.has(itemKey)) return;

    let messageData: { title: string; message: string } | null = null;

    // Проверяем, есть ли подарок на этой клетке
    if (tile.gift) {
      messageData = getMessageForGift(tile.gift.type, tile.gift.text);
    }
    // Проверяем, есть ли интерактивный объект
    else if (
      tile.seaObject &&
      INTERACTIVE_OBJECTS.includes(tile.seaObject.type)
    ) {
      messageData = getMessageForSeaObject(
        tile.seaObject.type,
        tile.seaObject.text,
        tile.seaObject.age
      );
    }

    // Если нашли сообщение, показываем его
    if (messageData) {
      setIsProcessingClick(true);
      console.log("[TreasurePage] Message found, code:", code);

      // Помечаем элемент как открытый синхронно (в ref) и асинхронно (в state)
      openedItemsRef.current.add(itemKey);
      setOpenedItems((prev) => new Set(prev).add(itemKey));

      // Сохраняем историю активации с открытым посланием
      await saveActivationHistory(
        x,
        y,
        tile,
        messageData.title,
        messageData.message
      );

      setCurrentMessage(messageData);
      setShowMessageModal(true);

      // Удаляем элемент с карты и сохраняем на сервере
      const newMap = tileMap.map((row, rowIndex) =>
        row.map((tile, colIndex) => {
          if (rowIndex === y && colIndex === x) {
            // Используем оригинальный tileCode клетки, чтобы сохранить базовый тип (вода/песок)
            // Это важно, чтобы песок оставался песком, а вода - водой
            let baseTileCode = tile.tileCode;

            // Если tileCode не установлен или это интерактивный объект, определяем базовый тип
            if (!tile.tileCode || tile.tileCode === SeaObjectType.ISLAND_SAND_CHEST || tile.tileCode === SeaObjectType.CHEST_SEA) {
              // Если это сундук на воде - всегда возвращаем воду
              if (tile.tileCode === SeaObjectType.CHEST_SEA) {
                baseTileCode = SeaObjectType.WATER;
              } else if (tile.seaObject) {
                // Проверяем, был ли объект песчаным
                if (PLAYER_SAND_OBJECTS.includes(tile.seaObject.type)) {
                  baseTileCode = SeaObjectType.SAND_ISLAND;
                } else if (PLAYER_WATER_OBJECTS.includes(tile.seaObject.type)) {
                  baseTileCode = SeaObjectType.WATER;
                }
              } else if (tile.gift) {
                // Подарки всегда размещаются на песке
                baseTileCode = SeaObjectType.SAND_ISLAND;
              }
            }

            // Очищаем клетку - возвращаем к базовому типу
            return { tileCode: baseTileCode };
          }
          return { ...tile };
        })
      );

      setTileMap(newMap);

      // Сохраняем обновленную карту на сервере
      try {
        await treasureHuntApi.saveMap(newMap);
      } catch (err) {
        console.error("Не удалось сохранить изменения карты на сервере:", err);
      }

      setIsProcessingClick(false);
    }
  };

  // Получение сообщения для подарка
  const getMessageForGift = (
    giftType: GiftType,
    giftText: string
  ): { title: string; message: string } => {
    const titles = {
      ka: {
        [GiftType.CHEST]: "განძი 🎁",
        [GiftType.LETTER]: "შეტყობინება 💌",
        [GiftType.HEART]: "რომანტიკული სიურპრიზი 💖",
      },
      en: {
        [GiftType.CHEST]: "Treasure 🎁",
        [GiftType.LETTER]: "Message 💌",
        [GiftType.HEART]: "Romantic Surprise 💖",
      },
    };

    return {
      title: titles[simulationLanguage][giftType] || GIFT_NAMES[giftType],
      message: giftText,
    };
  };

  // Получение сообщения для морского объекта
  const getMessageForSeaObject = (
    objectType: SeaObjectType,
    customText?: string,
    age?: number
  ): { title: string; message: string } => {
    // Если есть кастомный текст, показываем его вместо стандартного сообщения
    if (customText) {
      // Добавляем возраст в начало сообщения, если он указан
      const agePrefix = age
        ? simulationLanguage === "ka"
          ? `👤 ასაკი: ${age}\n\n`
          : `👤 Age: ${age}\n\n`
        : "";
      return {
        title: simulationLanguage === "ka" ? "შეტყობინება 💌" : "Message 💌",
        message: agePrefix + customText,
      };
    }
    const messages: Record<
      "ka" | "en",
      Partial<Record<SeaObjectType, { title: string; message: string }>>
    > = {
      ka: {
        [SeaObjectType.BOTTLE]: {
          title: "ბოთლი ზღვაში 🍾",
          message:
            "თქვენ იპოვეთ ბოთლი შეტყობინებით! შიგნით ჩანაწერია იდუმალი ადგილის კოორდინატებით.",
        },
        [SeaObjectType.BOTTLE_SAND]: {
          title: "ბოთლი ქვიშაზე 🍾",
          message:
            "ძველი ბოთლი, გადმოგდებული ნაპირზე. შიგნით შემორჩენილია მოგზაურის შეტყობინება.",
        },
        [SeaObjectType.ISLAND_SAND_CHEST]: {
          title: "საგანძური 💎",
          message:
            "უძველესი ყუთი, სავსე ოქროს მონეტებითა და ძვირფასეულობებით! ეს ნამდვილი განძია!",
        },
        [SeaObjectType.CHEST_SEA]: {
          title: "საგანძური ზღვაში 💎",
          message:
            "დაძირული გემის საგანძური! სავსე ოქროს მონეტებითა და ძვირფასი საგნებით!",
        },
        [SeaObjectType.WOMAN_SAND_LIES]: {
          title: "შეხვედრა პლაჟზე 👩",
          message:
            "თქვენ შეხვდით საინტერესო ადამიანს პლაჟზე. დაიწყო სასიამოვნო საუბარი!",
        },
        [SeaObjectType.WOMAN_SAND_LIES2]: {
          title: "შეხვედრა პლაჟზე 👩",
          message:
            "ახალი ნაცნობობა! ვინ იცის, შესაძლოა ეს დიდი მეგობრობის დასაწყისია.",
        },
        [SeaObjectType.WOMAN_SEA_LIES]: {
          title: "შეხვედრა წყალში 👩",
          message:
            "თქვენ შეხვდით ვიღაცას ზღვაში. ერთად ისიამოვნეთ მშვენიერი ჩასვლით.",
        },
        [SeaObjectType.MAN_SAND_LIES]: {
          title: "შეხვედრა ნაპირზე 👨",
          message:
            "მოულოდნელი შეხვედრა ზღვის ნაპირზე. საინტერესო საუბარი მოგზაურობებზე!",
        },
        [SeaObjectType.MAN_SEA_LIES]: {
          title: "შეხვედრა ზღვაში 👨",
          message:
            "თქვენ გაიცანით ვიღაც ცურვის დროს. გაცვალეთ ამბები თავგადასავლებზე.",
        },
        [SeaObjectType.MAN_SAND]: {
          title: "ახალი ნაცნობობა 👨",
          message:
            "შეხვედრა საინტერესო ადამიანთან პლაჟზე. შესაძლოა კიდევ შეხვდეთ!",
        },
      },
      en: {
        [SeaObjectType.BOTTLE]: {
          title: "Bottle at Sea 🍾",
          message:
            "You found a bottle with a message! Inside is a note with coordinates of a mysterious place.",
        },
        [SeaObjectType.BOTTLE_SAND]: {
          title: "Bottle on Sand 🍾",
          message:
            "An old bottle washed ashore. Inside is a preserved message from a traveler.",
        },
        [SeaObjectType.ISLAND_SAND_CHEST]: {
          title: "Treasure Chest 💎",
          message:
            "An ancient chest full of gold coins and jewels! This is a real treasure!",
        },
        [SeaObjectType.CHEST_SEA]: {
          title: "Treasure at Sea 💎",
          message:
            "A sunken ship's treasure! Full of gold coins and precious items!",
        },
        [SeaObjectType.WOMAN_SAND_LIES]: {
          title: "Beach Encounter 👩",
          message:
            "You met an interesting person on the beach. A pleasant conversation began!",
        },
        [SeaObjectType.WOMAN_SAND_LIES2]: {
          title: "Beach Encounter 👩",
          message:
            "A new acquaintance! Who knows, maybe this is the beginning of a great friendship.",
        },
        [SeaObjectType.WOMAN_SEA_LIES]: {
          title: "Meeting in Water 👩",
          message:
            "You met someone in the sea. Together you enjoyed a beautiful sunset.",
        },
        [SeaObjectType.MAN_SAND_LIES]: {
          title: "Shore Encounter 👨",
          message:
            "An unexpected meeting on the seashore. Interesting conversation about travels!",
        },
        [SeaObjectType.MAN_SEA_LIES]: {
          title: "Sea Meeting 👨",
          message:
            "You met someone while swimming. Exchanged stories about adventures.",
        },
        [SeaObjectType.MAN_SAND]: {
          title: "New Acquaintance 👨",
          message:
            "Meeting with an interesting person on the beach. Maybe you'll see each other again!",
        },
      },
    };

    const defaultMessage = {
      title: simulationLanguage === "ka" ? "სიურპრიზი! 🎉" : "Surprise! 🎉",
      message:
        simulationLanguage === "ka"
          ? "თქვენ იპოვეთ რაღაც საინტერესო!"
          : "You found something interesting!",
    };

    return messages[simulationLanguage][objectType] || defaultMessage;
  };

  // Проверка, является ли клетка интерактивной (есть ли на ней подарок или интерактивный объект)
  const isInteractiveTile = (x: number, y: number): boolean => {
    if (!tileMap[y] || !tileMap[y][x]) return false;

    const tile = tileMap[y][x];
    const itemKey = `${x}-${y}`;

    // Уже открытые элементы не интерактивны
    if (openedItems.has(itemKey)) return false;

    // Проверяем только после раскрытия посланий
    if (!giftsRevealed) return false;

    // В режиме размещения не показываем обводку существующих элементов
    if (isPlacingMessage || isPlacingAcquaintance) return false;

    // Если есть облако - клетка интерактивна (можно открыть)
    if (tile.hasClouds) return true;

    // Проверяем, есть ли подарок
    if (tile.gift !== undefined) return true;

    // Проверяем, есть ли интерактивный объект
    if (tile.seaObject && INTERACTIVE_OBJECTS.includes(tile.seaObject.type)) {
      return true;
    }

    return false;
  };

  // Полноэкранный экран загрузки
  if (isMapLoading) {
    return (
      <div
        className="flex flex-col items-center justify-center w-screen h-screen relative overflow-hidden"
        style={{
          backgroundColor: "#fef3c7", // amber-100
        }}
      >
        {/* Картинка превью */}
        <img
          src="/models/preview-game.png"
          alt="Treasure Hunt Preview"
          className="w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 object-contain mb-4"
          style={{ imageRendering: "pixelated" }}
        />

        {/* Название */}
        <h1
          className="text-xl sm:text-2xl md:text-3xl font-bold text-amber-900 mb-4"
          style={{ fontFamily: "MinecraftGeorgian, monospace" }}
        >
          Treasure Hunt
        </h1>

        {/* Прогресс-бар */}
        <div className="w-32 sm:w-40 md:w-48 h-2 bg-amber-200 rounded-full border-2 border-amber-800 overflow-hidden">
          <div
            className="h-full bg-amber-600 rounded-full"
            style={{
              animation: "progressLoading 1.5s ease-out infinite",
            }}
          />
        </div>

        <style>{`
          @keyframes progressLoading {
            0% { width: 0%; }
            50% { width: 50%; }
            100% { width: 0%; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div
      className="flex items-center justify-center w-screen h-screen relative overflow-hidden"
      style={{
        backgroundImage: "url(/water.gif)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Затемняющий оверлей */}
      <div className="absolute inset-0 bg-black/30 z-0" />

      <div className="relative z-10 w-full h-full">
        {/* Панель с элементами слева */}
        <ElementsLegend language={simulationLanguage} />

        {/* Контейнер с кнопками справки и смены языка */}
        <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-20 flex items-center gap-2 sm:gap-3">
          {/* Переключатель языка */}
          <LanguageToggle
            language={simulationLanguage}
            onLanguageChange={handleLanguageChange}
          />

          {/* Кнопка открытия правил */}
          <button
            onClick={() => setShowRulesModal(true)}
            className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center transition-all hover:scale-110 "
            title={simulationLanguage === "ka" ? "თამაშის წესები" : "Game Rules"}
            style={{
              backgroundImage: "url(/models/treasure-hunt/ui/quest.png)",
              backgroundSize: "contain",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              imageRendering: "pixelated",
            }}
          />
        </div>

        {/* Подсказка о перемещении карты */}
        <div className="absolute bottom-4 left-4 sm:bottom-6 sm:left-6 z-20 flex items-center gap-2 sm:gap-3 bg-black/50 rounded-lg px-3 py-2 sm:px-4 sm:py-3 backdrop-blur-sm">
          <img
            src="/models/treasure-hunt/ui/hand.png"
            alt="Hand"
            className="w-8 h-8 sm:w-10 sm:h-10"
            style={{ imageRendering: "pixelated" }}
          />
          <p className="text-white text-xs sm:text-sm font-medium" style={{ fontFamily: "MinecraftGeorgian, monospace" }}>
            {simulationLanguage === "ka"
              ? "გადაადგილება შეჭიდული თითით"
              : simulationLanguage === "en"
              ? "Move with pressed finger"
              : "Перемещение зажатым пальцем"}
          </p>
        </div>

        {/* Контейнер с картой с поддержкой drag */}
        <MapViewport
          mapWidth={GRID_COLS * tileSize}
          mapHeight={GRID_ROWS * tileSize}
        >
          <GameField
            tileMap={tileMap}
            hoveredTile={hoveredTile}
            onTileClick={handleTileClick}
            showClouds={true}
            cloudEditMode={false}
            onTileHover={(x, y, event) => {
              setHoveredTile({ x, y });
              const tile = tileMap[y]?.[x];
              // Проверяем, есть ли на тайле человек с возрастом
              if (
                tile?.seaObject &&
                AGE_OBJECTS.includes(tile.seaObject.type) &&
                tile.seaObject.age !== undefined
              ) {
                setTooltipAge(tile.seaObject.age);
                if (event) {
                  setTooltipPosition({ x: event.clientX, y: event.clientY });
                }
              } else {
                setTooltipAge(null);
              }
            }}
            onTileLeave={() => {
              setHoveredTile(null);
              setTooltipAge(null);
            }}
            onRotate={() => {}}
            showRotateHint={false}
            interactiveTiles={tileMap.map((row, y) =>
              row.map((_, x) => isInteractiveTile(x, y))
            )}
            tileSize={tileSize}
          />
        </MapViewport>
        {/* Модальное окно выбора типа послания только если giftsRevealed */}
        {giftsRevealed &&
          !showMessageModal &&
          !showWelcomeModal &&
          !showRulesModal &&
          !showCodeModal &&
          !isPlacingMessage &&
          !isPlacingAcquaintance && (
            <SelectMessageTypeModal
              isOpen={true}
              language={simulationLanguage}
              onBack={() => {
                setGiftsRevealed(false);
                setShowActionPanel(true);
              }}
            />
          )}
      </div>
      {/* Модальное окно приветствия */}
      <WelcomeModal
        isOpen={showWelcomeModal}
        language={simulationLanguage}
        onLanguageChange={handleLanguageChange}
        onClose={handleWelcomeClose}
      />

      {/* Модальное окно с правилами */}
      <RulesModal
        isOpen={showRulesModal}
        language={simulationLanguage}
        onLanguageChange={handleLanguageChange}
        onClose={() => setShowRulesModal(false)}
      />
      {/* Модальное окно ввода кода (внизу экрана) */}
      <PromoCodeModal
        isOpen={showCodeModal}
        language={simulationLanguage}
        onSubmit={handleCodeSubmit}
      />
      {/* Панель действий после ввода кода */}
      <ActionPanel
        isOpen={showActionPanel}
        language={simulationLanguage}
        code={code}
        onOpenGifts={handleOpenGifts}
        onLeaveMessage={handleLeaveMessage}
        onLeaveAcquaintance={handleLeaveAcquaintance}
        onCancel={handleCancelAction}
        onLanguageChange={handleLanguageChange}
      />
      {/* Модальное окно ввода текста сообщения */}
      <LeaveMessageTextOnlyModal
        isOpen={showLeaveMessageTextModal}
        language={simulationLanguage}
        onSave={handleSaveMessageText}
        onCancel={handleCancelLeaveMessage}
      />
      {/* Модальное окно формы знакомства */}
      <LeaveAcquaintanceFormModal
        isOpen={showLeaveAcquaintanceFormModal}
        language={simulationLanguage}
        onSave={handleSaveAcquaintanceData}
        onCancel={handleCancelLeaveMessage}
      />
      {/* Индикатор режима размещения сообщения */}
      {isPlacingMessage && (
        <div className="fixed bottom-0 left-0 right-0 flex items-end justify-center z-50 p-4 pointer-events-none">
          <div
            className="w-full max-w-md mb-4 sm:mb-8 pointer-events-auto bg-amber-100 rounded-xl border-4 border-amber-800 shadow-2xl px-6 py-6 sm:px-8 sm:py-6"
            style={{
              fontFamily: "MinecraftGeorgian, monospace",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg sm:text-xl font-bold text-amber-900 mb-3 text-center">
              {simulationLanguage === "ka"
                ? "💌 შეტყობინების განთავსება"
                : "💌 Placing Message"}
            </h2>

            <p className="text-amber-900 text-xs sm:text-sm mb-4 text-center whitespace-pre-wrap">
              {simulationLanguage === "ka"
                ? "დააწკაპუნეთ ცარიელ წყლის ან ქვიშის უჯრედზე (ღრუბლების გარეშე)"
                : "Click on an empty water or sand tile (without clouds)"}
            </p>

            <div className="flex flex-col gap-3">
              <button
                onClick={handleCancelLeaveMessage}
                className="w-full px-4 py-2 bg-gradient-to-b from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white font-bold rounded-none border-4 border-amber-900 shadow-[inset_0_2px_0_rgba(255,255,255,0.3),inset_0_-2px_0_rgba(0,0,0,0.3)] hover:shadow-[inset_0_2px_0_rgba(255,255,255,0.3),inset_0_-2px_0_rgba(0,0,0,0.5)] transition-all active:translate-y-0.5 active:shadow-[inset_0_1px_0_rgba(0,0,0,0.3)] opacity-80 hover:opacity-100"
                style={{ fontFamily: "MinecraftGeorgian, monospace" }}
              >
                <span className="text-xs sm:text-sm">
                  {simulationLanguage === "ka" ? "გაუქმება" : "Cancel"}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Индикатор режима размещения знакомства */}
      {isPlacingAcquaintance && (
        <div className="fixed bottom-0 left-0 right-0 flex items-end justify-center z-50 p-4 pointer-events-none">
          <div
            className="w-full max-w-md mb-4 sm:mb-8 pointer-events-auto bg-amber-100 rounded-xl border-4 border-amber-800 shadow-2xl px-6 py-6 sm:px-8 sm:py-6"
            style={{
              fontFamily: "MinecraftGeorgian, monospace",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg sm:text-xl font-bold text-amber-900 mb-3 text-center">
              {simulationLanguage === "ka"
                ? "👥 გაცნობის განთავსება"
                : "👥 Placing Acquaintance"}
            </h2>

            <p className="text-amber-900 text-xs sm:text-sm mb-4 text-center whitespace-pre-wrap">
              {simulationLanguage === "ka"
                ? "დააწკაპუნეთ ცარიელ წყლის ან ქვიშის უჯრედზე (ღრუბლების გარეშე)"
                : "Click on an empty water or sand tile (without clouds)"}
            </p>

            <div className="flex flex-col gap-3">
              <button
                onClick={handleCancelLeaveMessage}
                className="w-full px-4 py-2 bg-gradient-to-b from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white font-bold rounded-none border-4 border-amber-900 shadow-[inset_0_2px_0_rgba(255,255,255,0.3),inset_0_-2px_0_rgba(0,0,0,0.3)] hover:shadow-[inset_0_2px_0_rgba(255,255,255,0.3),inset_0_-2px_0_rgba(0,0,0,0.5)] transition-all active:translate-y-0.5 active:shadow-[inset_0_1px_0_rgba(0,0,0,0.3)] opacity-80 hover:opacity-100"
                style={{ fontFamily: "MinecraftGeorgian, monospace" }}
              >
                <span className="text-xs sm:text-sm">
                  {simulationLanguage === "ka" ? "გაუქმება" : "Cancel"}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Модальное окно с сообщением (по центру экрана) */}
      <MessageModal
        isOpen={showMessageModal}
        language={simulationLanguage}
        title={currentMessage.title}
        message={currentMessage.message}
        onClose={handleCloseMessage}
      />
      {/* Модальное окно компаса */}
      <CompassModal
        isOpen={showCompassModal}
        language={simulationLanguage}
        angle={compassAngle}
        position={compassPosition}
        tileSize={tileSize}
        onClose={handleCloseCompass}
      />
      {/* Всплывающая подсказка с возрастом */}
      {tooltipAge !== null && (
        <div
          className="fixed z-50 bg-amber-100 px-3 py-2 border-2 border-amber-800 shadow-lg pointer-events-none"
          style={{
            left: `${tooltipPosition.x + 15}px`,
            top: `${tooltipPosition.y - 50}px`,
            transform: "translateY(-100%)",
            fontFamily: "MinecraftGeorgian, monospace",
            imageRendering: "pixelated",
          }}
        >
          <div className="text-xs sm:text-sm">
            <span className="font-bold text-amber-900">
              {simulationLanguage === "ka" ? "ასაკი:" : "Age:"}
            </span>{" "}
            <span className="text-amber-800">{tooltipAge}</span>
          </div>
        </div>
      )}
    </div>
  );
};
